/*
  Flowkeeper - Pomodoro timer for power users and teams
  Copyright (c) 2023 Constantine Kulak, Geoffrey Espagne

  This program is free software: you can redistribute it and/or modify
  it under the terms of the GNU General Public License as published by
  the Free Software Foundation; either version 3 of the License, or
  (at your option) any later version.

  This program is distributed in the hope that it will be useful,
  but WITHOUT ANY WARRANTY; without even the implied warranty of
  MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
  GNU General Public License for more details.

  You should have received a copy of the GNU General Public License
  along with this program.  If not, see <https://www.gnu.org/licenses/>.
*/

import React, {useContext, useRef, useState} from 'react';

import {
    ActionGroup,
    Button,
    ButtonType,
    ButtonVariant,
    DataListAction,
    DataListCell,
    DataListItem,
    DataListItemCells,
    DataListItemRow,
    Divider,
    Dropdown,
    DropdownItem,
    DropdownList,
    Form,
    FormContextProvider,
    MenuToggle,
    MenuToggleElement,
    Modal,
    ModalVariant,
    TextInput
} from '@patternfly/react-core';
import {executeAndSend} from "../../fk/core/websocket_event_source";
import {CompleteWorkitem, DeleteWorkitem, RenameWorkitem} from '../../fk/core/workitem';
import EllipsisVIcon from '@patternfly/react-icons/dist/esm/icons/ellipsis-v-icon';
import EditIcon from '@patternfly/react-icons/dist/esm/icons/edit-icon';
import TrashIcon from '@patternfly/react-icons/dist/esm/icons/trash-icon';
import CheckIcon from '@patternfly/react-icons/dist/esm/icons/check-icon';
import DataContext from '../context/DataContext';

type WorkItemDataListItemProps = {
    id: string,
    name: string,
    state: string
}

export function WorkItemDataListItem(props: WorkItemDataListItemProps) {

    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const ref = useRef<HTMLInputElement>(null);

    const { data, setData } = useContext(DataContext);

    const renameWorkitem = (id: string, newName: string) => {
        if (data) {
            executeAndSend(setData, RenameWorkitem, id, newName);
        } else {
            throw new Error("Trying to rename a workitem when there's no data");
        }
    }

    const deleteWorkitem = (id: string) => {
        if (data) {
            executeAndSend(setData, DeleteWorkitem, id);
        } else {
            throw new Error("Trying to delete a workitem when there's no data");
        }
    }

    const completeWorkitem = (id: string) => {
        if (data) {
            executeAndSend(setData, CompleteWorkitem, id, 'finished');
        } else {
            throw new Error("Trying to complete a workitem when there's no data");
        }
    }

    const onDropdownToggle = () => {
        setIsDropdownOpen(!isDropdownOpen);
    };

    const onDropdownSelect = () => {
        setIsDropdownOpen(!isDropdownOpen);
    };

    const deleteWorkitemModal = () => {
        return (
            <Modal
                variant={ModalVariant.medium}
                title={"Confirmation"}
                titleIconVariant="danger"
                isOpen={isDeleteModalOpen}
                onClose={handleCancelDelete}
                actions={[
                    <Button key="confirm" variant="danger" onClick={onAcceptDelete}>
                        Delete
                    </Button>,
                    <Button key="cancel" variant="link" onClick={handleCancelDelete}>
                        Cancel
                    </Button>
                ]}>
                Are you sure you want to delete "{props.name}"?
            </Modal>
        );
    }

    const handleCancelDelete = (_event: KeyboardEvent | React.MouseEvent) => {
        setIsDeleteModalOpen(!isDeleteModalOpen);
    };

    const onAcceptDelete = () => {
        setIsDeleteModalOpen(!isDeleteModalOpen);
        deleteWorkitem(props.id)
    }

    const editWorkitemModal = () => {
        return (
            <Modal
                variant={ModalVariant.medium}
                title={"Edit Item"}
                isOpen={isEditModalOpen}
                onClose={handleCancelEdit}>
                <FormContextProvider initialValues={{'workitem-name': props.name}}>
                    {({setValue, getValue, setError}) => (
                        <Form>
                            <TextInput
                                id="workitem-name"
                                ref={ref}
                                onFocus={() => ref?.current?.select()}
                                onChange={(_event, value) => {
                                    setValue('workitem-name', value);
                                    setError('workitem-name', undefined);
                                }}
                                value={getValue('workitem-name')}/>
                            <ActionGroup>
                                <Button
                                    type={ButtonType.submit}
                                    onClick={(e) => {
                                        e.preventDefault();
                                        onAcceptEdit(getValue('workitem-name'));
                                    }}>Save</Button>
                                <Button
                                    type={ButtonType.button}
                                    variant={ButtonVariant.link}
                                    onClick={(e) => {
                                        e.preventDefault();
                                        handleCancelEdit(e);
                                    }}>Cancel</Button>
                            </ActionGroup>
                        </Form>)}
                </FormContextProvider>
            </Modal>
        );
    }

    const handleCancelEdit = (_event: KeyboardEvent | React.MouseEvent) => {
        setIsEditModalOpen(!isEditModalOpen);
    };

    const onAcceptEdit = (newName: string) => {
        renameWorkitem(props.id, newName);
        setIsEditModalOpen(!isEditModalOpen);
    }

    const onCompleteWorkitem = () => {
        completeWorkitem(props.id);
    }

    return (
        <>
            {deleteWorkitemModal()}
            {editWorkitemModal()}
            <DataListItem aria-labelledby={props.id + '-item'} id={props.id}>
                <DataListItemRow>
                    <DataListItemCells
                        dataListCells={[
                            <DataListCell key={props.id + '-item'}>
                                <span id={props.id + '-item1'} className={props.state === 'finished' ? 'wi-finished' : ''}>{props.name}</span>
                            </DataListCell>,
                        ]}/>
                    <DataListAction
                        aria-labelledby="multi-actions-item1 multi-actions-action1"
                        id={props.id + "item-actions"}
                        aria-label="Actions"
                        isPlainButtonAction>
                        <Dropdown
                            popperProps={{ position: 'right' }}
                            onSelect={onDropdownSelect}
                            toggle={(toggleRef: React.Ref<MenuToggleElement>) => (
                                <MenuToggle
                                    ref={toggleRef}
                                    isExpanded={isDropdownOpen}
                                    onClick={onDropdownToggle}
                                    variant="plain"
                                    aria-label="Data list with actions example kebab toggle">
                                    <EllipsisVIcon aria-hidden="false" />
                                </MenuToggle>
                            )}
                            isOpen={isDropdownOpen}
                            onOpenChange={(isOpen: boolean) => setIsDropdownOpen(isOpen)}>
                            <DropdownList>
                                <DropdownItem
                                    key="edit"
                                    icon={<EditIcon />}
                                    isDisabled={props.state === 'finished'}
                                    onClick={(event: any) => {
                                        event.preventDefault();
                                        setIsEditModalOpen(true);
                                    }}>Edit</DropdownItem>
                                <DropdownItem
                                    key="delete"
                                    icon={<TrashIcon />}
                                    isDanger
                                    onClick={(event: any) => {
                                        event.preventDefault();
                                        setIsDeleteModalOpen(true);
                                    }}>Delete</DropdownItem>
                                <Divider />
                                <DropdownItem
                                    key="complete"
                                    icon={<CheckIcon />}
                                    isDisabled={props.state === 'finished'}
                                    onClick={(event: any) => {
                                        event.preventDefault();
                                        onCompleteWorkitem();
                                    }}>Complete</DropdownItem>
                            </DropdownList>
                        </Dropdown>
                    </DataListAction>
                </DataListItemRow>
            </DataListItem>
        </>
    );
}
